import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  useLocation,
  useRouterState,
} from '@tanstack/react-router'
import { QueryClientProvider } from '@tanstack/react-query'
import { Suspense, lazy, useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useI18nMeta } from '../hooks/useI18nMeta'
import { useAnalytics } from '../hooks/useAnalytics'
import { useWebMcp } from '@/hooks/useWebMcp'

import type { QueryClient } from '@tanstack/react-query'
import type { i18n as I18nInstance } from 'i18next'
import { LedgerFooter } from '@/components/ledger/LedgerFooter'
import { LedgerNav } from '@/components/ledger/LedgerNav'
import { ThemeProvider } from '@/components/theme-provider'
import { CRTSoundProvider } from '@/hooks/useCRTSound'
import { AccountAuthBoundary } from '@/components/layout/AccountAuthBoundary'
import { AppShellContext } from '@/components/layout/AppShellContext'
import { RouteErrorBoundary } from '@/components/errors/RouteErrorBoundary'
import { AppErrorBoundary } from '@/components/errors/AppErrorBoundary'
import { LoadingState } from '@/components/layout/LoadingState'
import { OfflineFallback } from '@/components/layout/OfflineFallback'
import { ChatProvider } from '@/contexts/ChatContext'
import { CookieConsent } from '@/components/CookieConsent'

import appCss from '@/styles.css?url'
import journalCss from '@/journal.css?url'
import { isJournalPath } from '@/lib/journal-route'
import {
  getLocaleDir,
  getLocaleFromPath,
  stripLocalePrefix,
} from '@/lib/i18n-utils'
import { isPrivateRoute } from '@/lib/seo-head'
import { COMPANY, SEO_ORIGIN } from '@/lib/company'
import { jsonLd, organizationLd, seoStrings, websiteLd } from '@/lib/seo-head'

// Admin notifications are not part of the public shell. Load them only when rendered.
const Toaster = lazy(() => import('@/components/ui/toaster').then(m => ({ default: m.Toaster })))

const NotFoundPage = lazy(() =>
  import('@/components/errors/NotFoundPage').then((m) => ({
    default: m.NotFoundPage,
  })),
)

// Dev-only: lazy-load devtools so they are tree-shaken from production builds
const TanStackRouterDevtools = import.meta.env.DEV
  ? lazy(() =>
      import('@tanstack/react-router-devtools').then((m) => ({
        default: m.TanStackRouterDevtools,
      })),
    )
  : () => null

const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(() =>
      import('@tanstack/react-query-devtools').then((m) => ({
        default: m.ReactQueryDevtools,
      })),
    )
  : () => null

interface MyRouterContext {
  queryClient: QueryClient
  i18n: I18nInstance
}

function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(true)

  useEffect(() => {
    setIsOnline(navigator.onLine)
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  return isOnline
}

function RootComponent() {
  useI18nMeta()
  useAnalytics()
  useWebMcp()
  const { t } = useTranslation()
  const location = useLocation()
  const isOnline = useOnlineStatus()
  const { queryClient } = Route.useRouteContext()

  const publicPath = stripLocalePrefix(location.pathname)
  // Public pages are rendered by the Ledger shell. Account pages (sign-in, profile)
  // share that shell but still need the account auth boundary, so they are not
  // "ledger routes" for the boundary check below.
  const isLedgerRoute =
    publicPath.startsWith('/community') ||
    publicPath === '/agents/callback' ||
    publicPath === '/auth/callback' ||
    !isPrivateRoute(publicPath)
  const isJournalRoute = isJournalPath(location.pathname)
  const isAdminRoute = /\/admin(\/|$)/.test(location.pathname)

  const content = isAdminRoute ? (
    <>
      <Outlet />
      <Suspense fallback={null}><Toaster /></Suspense>
    </>
  ) : (
    <ChatProvider>
      <div className="ledger-body ledger-ground flex min-h-screen flex-col">
        <a href="#main-content" className="skip-link">
          {t('nav.skipToContent')}
        </a>
        <LedgerNav />
        <main
          id="main-content"
          tabIndex={-1}
          className="relative flex-1 overflow-x-hidden"
        >
          <div className={isJournalRoute ? 'journal-site' : undefined}>
            <Outlet />
          </div>
        </main>
        <LedgerFooter />
      </div>
    </ChatProvider>
  )

  return (
    <ThemeProvider defaultTheme="system" storageKey="horizon-tech-theme">
      <CRTSoundProvider>
        <QueryClientProvider client={queryClient}>
          <AccountAuthBoundary enabled={!isLedgerRoute}>
            <AppErrorBoundary>
              {!isOnline && <OfflineFallback />}
              <Suspense
                fallback={
                  isOnline ? (
                    <LoadingState fullPage message="Loading Horizon Tech..." />
                  ) : (
                    <OfflineFallback fullPage />
                  )
                }
              >
                <AppShellContext.Provider value={true}>{content}</AppShellContext.Provider>
              </Suspense>
              <CookieConsent />
            </AppErrorBoundary>
          </AccountAuthBoundary>
          <Suspense fallback={null}>
            <TanStackRouterDevtools />
            <ReactQueryDevtools buttonPosition="bottom-right" />
          </Suspense>
        </QueryClientProvider>
      </CRTSoundProvider>
    </ThemeProvider>
  )
}

// Blocking inline script that applies the theme class before first paint,
// eliminating the light→dark CLS flash that happens when ThemeProvider's
// useEffect runs after hydration.
const THEME_INIT_SCRIPT = `(function(){var t=localStorage.getItem('horizon-tech-theme');var d=t==='dark'||(t!=='light'&&matchMedia('(prefers-color-scheme:dark)').matches);document.documentElement.classList.toggle('dark',d);document.documentElement.classList.toggle('light',!d);document.documentElement.style.colorScheme=d?'dark':'light'})()`

function RootDocument({ children }: { children: React.ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const locale = getLocaleFromPath(pathname)
  return (
    <html lang={locale} dir={getLocaleDir(locale)} suppressHydrationWarning>
      <head>
        <script dangerouslySetInnerHTML={{ __html: THEME_INIT_SCRIPT }} />
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  )
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
  head: ({ matches, match }) => {
    const pathname = matches[matches.length - 1]?.pathname ?? '/'
    const locale = getLocaleFromPath(pathname)
    const isNotFound = match.globalNotFound === true
    const site = seoStrings('default', locale)
    return {
      meta: [
        { charSet: 'utf-8' },
        {
          name: 'viewport',
          content:
            'width=device-width, initial-scale=1, interactive-widget=resizes-content',
        },
        { name: 'theme-color', content: '#f3efe6' },
        { title: site.title },
        { name: 'description', content: site.description },
        { name: 'author', content: COMPANY.legalName },
        {
          name: 'robots',
          content: isNotFound ? 'noindex,follow' : 'index,follow',
        },
        { property: 'og:type', content: 'website' },
        { property: 'og:site_name', content: COMPANY.brand },
        { property: 'og:title', content: site.title },
        { property: 'og:description', content: site.description },
        { property: 'og:image', content: COMPANY.ogImage },
        // NOTE: no static og:url here on purpose — canonical and og:url are
        // emitted per page by the `{-$locale}` layout head (buildLocalizedSeoHead).
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:image', content: COMPANY.ogImage },
      ],
      links: [
        // Preserve the original cascade: journal rules precede shared styles.
        // Company, cloud and community pages do not use the journal shell.
        ...(isJournalPath(pathname) ? [{ rel: 'stylesheet', href: journalCss }] : []),
        { rel: 'stylesheet', href: appCss },
        // The body and display faces are visible immediately on public pages.
        // Match CSS font URLs and CORS mode so preload requests are reused.
        ...['manrope-normal-400-latin', 'spectral-normal-500-latin'].map(font => ({
          rel: 'preload', as: 'font', href: `/fonts/${font}.woff2`,
          type: 'font/woff2', crossOrigin: 'anonymous' as const,
        })),
        { rel: 'icon', type: 'image/png', href: '/favicon-32.png' },
        { rel: 'apple-touch-icon', href: '/apple-touch-icon.png' },
        { rel: 'manifest', href: '/manifest.json' },
        // NOTE: no static canonical here on purpose — canonical and og:url are
        // emitted per page by the `{-$locale}` layout head (buildLocalizedSeoHead).
        {
          rel: 'alternate',
          type: 'application/rss+xml',
          title: 'Horizon Tech — Blog',
          href: `${SEO_ORIGIN}/rss.xml`,
        },
        {
          rel: 'preload',
          as: 'image',
          href: '/logo-64.webp',
          type: 'image/webp',
        },
      ],
      scripts: [jsonLd(organizationLd(locale)), jsonLd(websiteLd(locale))],
    }
  },
  shellComponent: RootDocument,
  component: RootComponent,
  notFoundComponent: () => (
    <Suspense fallback={<LoadingState fullPage message="Loading..." />}>
      <NotFoundPage />
    </Suspense>
  ),
  errorComponent: RouteErrorBoundary,
})
